import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Container,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import { API_URL } from '../config';

const InvitationAccept = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invitation, setInvitation] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }

    fetchInvitationDetails();
  }, [token]);

  const fetchInvitationDetails = async () => {
    try {
      const response = await fetch(`${API_URL}/api/v1/crew/invitations/${token}`);
      if (!response.ok) {
        throw new Error('Invalid or expired invitation');
      }
      const data = await response.json();
      setInvitation(data);
    } catch (error) {
      console.error('Error fetching invitation:', error);
      setError(error.message || 'Failed to load invitation details');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = () => {
    // For new users, redirect to register page with invitation token
    if (!invitation.userExists) {
      navigate(`/register?token=${token}&email=${encodeURIComponent(invitation.email)}`);
      return;
    }

    // For existing users, redirect to login with invitation token
    navigate(`/login?token=${token}&email=${encodeURIComponent(invitation.email)}`);
  };

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
          <Alert severity="error">{error}</Alert>
          <Button
            fullWidth
            variant="contained"
            onClick={() => navigate('/')}
            sx={{ mt: 2 }}
          >
            Return to Home
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
        <Typography component="h1" variant="h5" align="center" gutterBottom>
          Project Invitation
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          You've been invited to join <strong>{invitation.projectName}</strong> as a{' '}
          <strong>{invitation.role}</strong>
        </Alert>

        <Typography variant="body1" paragraph>
          {invitation.userExists
            ? 'Please sign in to accept this invitation.'
            : 'Please create an account to accept this invitation.'}
        </Typography>

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleAccept}
          >
            {invitation.userExists ? 'Sign In' : 'Create Account'}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            color="secondary"
            onClick={() => navigate('/')}
          >
            Cancel
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default InvitationAccept;
